import React, { useContext, useState, useEffect } from "react";
import styles from "./PartnerPanelPage.module.scss";

import { DEBUG, PATH } from '../../utils/constants';
import { computePeriodRange } from '../../utils/ComputePeriodRange/ComputePeriodRange';

import { getTotalPlayers } from '../../utils/api/usersAPI';
import { getStatsForGame, getAllGamesStats, getFinishedRuns } from '../../utils/api/progressionIndeAPI'; 
import { getGamesByUser } from '../../utils/api/partenairesAPI';
import { updateGame, regenCode } from '../../utils/api/gamesAPI';

import PartnerPanel from '../../components/PartnerPanel/PartnerPanel';

import { LanguageContext } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';

const debug = DEBUG;

const PartnerPanelPage = () => {

  const { user } = useAuth();

  // const idGame = 1;
  const [games, setGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState(null);
  // const [selectedGameName, setSelectedGameName] = useState("");
  const selectedGameName = React.useMemo(
    () => games.find(g => g.id_game === selectedGameId)?.nom || "",
    [games, selectedGameId]
  );
  const selectedGameCode = React.useMemo(
    () => games.find(g => g.id_game === selectedGameId)?.code || "",
    [games, selectedGameId]
  );

  const [total, setTotal] = useState(0);
  const [globalStats, setGlobalStats] = useState(null);
  const [infos, setInfos] = useState(null);
  const [loading, setLoading] = useState(true);

  const [finishedRuns, setFinishedRuns] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true); // utile pour désactiver le bouton si plus rien

  const [period, setPeriod] = useState('week');


  // 🔁 Initialisation : récupération des jeux associés
  useEffect(() => {
    async function fetchGames() {
      try {
        const gameList = await getGamesByUser(user?.id);
        setGames(gameList);

        if (gameList.length === 1) {
          setSelectedGameId(gameList[0].id_game);
          // setSelectedGameName(gameList[0].nom);
        }
      } catch (err) {
        console.error("Erreur récupération des jeux du partenaire :", err);
      }
    }

    fetchGames();
  }, []);


  // 🔁 Chargement des données quand un jeu est sélectionné
  useEffect(() => {
    if (!selectedGameId) return;

    async function fetchData() {
      setLoading(true);
      try {
        const { start, end } = computePeriodRange(period);
        if (debug) console.log(`[${period}] start: ${start}, end: ${end}`);
        const [users, stats, runs] = await Promise.all([
          getTotalPlayers(),
          getStatsForGame(selectedGameId, start, end),
          getFinishedRuns(selectedGameId, 1, 10)
        ]);

        setTotal(users.total);
        setGlobalStats(stats);
        setInfos(stats.datas);
        setFinishedRuns(runs.results);
        setPage(1);
        setHasMore(true);
      } catch (error) {
        console.error("Erreur chargement stats jeu :", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [selectedGameId, period]);

  // 🔁 Pagination : charger plus de runs
  const loadMoreRuns = async () => {
    const nextPage = page + 1;
    try {
      const response = await getFinishedRuns(selectedGameId, nextPage, 10);
      if (response.results.length === 0) {
        setHasMore(false);
      } else {
        setFinishedRuns(prev => [...prev, ...response.results]);
        setPage(nextPage);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des parcours supplémentaires :", error);
    }
  };

  // useEffect(() => {
  //     async function fetchData() {
  //     try {
  //       const [users, stats, runs] = await Promise.all([
  //         getTotalPlayers(),
  //         getStatsForGame(idGame, "2025-06-01", "2025-06-30"),
  //         getFinishedRuns(idGame, 1, 10)
  //       ]);

  //       setTotal(users.total);
  //       setGlobalStats(stats);
  //       setFinishedRuns(runs.results);
  //       setPage(1);
  //       setHasMore(true);
  //     } catch (error) {
  //       console.error("Erreur chargement initial :", error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   }

  //   fetchData();
  // }, []);

  
  const handleUpdateGame = async (gameId, payload) => {
    try {
      const res = await updateGame(gameId, payload); // POST /games/update
      if (!res?.success) return false;

      // Mets à jour localement si tu as un state du jeu courant :
      setInfos(prev => ({ ...prev, ...payload }));
      // (et si tu veux rafraîchir la liste 'games', fais-le ici)
      return true;
    } catch (e) {
      if (debug) console.error(e);
      return false;
    }
  };

  const handleRegenerateCode = async (gameId) => {
      const res = await regenCode(gameId);
      if (!res.code) throw new Error("Erreur");
      
      // Mets à jour la liste des jeux
      setGames(prev =>
        prev.map(g => g.id_game === gameId ? { ...g, code: res.code } : g)
      );
      return res.code;
    };


  return (
    <div className={styles.userAccount}>
      <h1>Espace partenaire</h1>

      {/* Sélecteur de jeu */}
      {games.length > 1 && (
        <div className={styles.gameSelector}>
          <label htmlFor="game-select">Sélectionnez un jeu :</label>
          <select
            id="game-select"
            value={selectedGameId || ''}
            onChange={(e) => setSelectedGameId(Number(e.target.value))}
          >
            <option value="" disabled>-- Choisir un jeu --</option>
            {games.map(game => (
              <option key={game.id_game} value={game.id_game}>
                {game.nom}
              </option>
            ))}
          </select>
        </div>
      )}

      {!selectedGameId && (
        <div className={styles.info}>
          <hr className={styles.hr} />
          <p>Aucun jeu n'est sélectionné pour le moment. Veuillez choisir un jeu.</p>
        </div>
      )}

      {/* Affichage du tableau d'administration si un jeu est sélectionné */}
      {selectedGameId && (
        <PartnerPanel
          gameName = {selectedGameName}
          totalUsers={total}
          globalStats={globalStats}
          finishedRuns={finishedRuns}
          loadMoreRuns={loadMoreRuns}
          hasMoreRuns={hasMore}
          period={period}
          setPeriod={setPeriod}
          companyInfo={infos || {}} // ou un objet { company_name, ... }
          onSaveCompanyInfo={(payload) => handleUpdateGame(selectedGameId, payload)} // appelle ton update
          gameCode = {selectedGameCode}
          onRegenerateCode={() => handleRegenerateCode(selectedGameId)}
        />
      )}

      {!selectedGameId && games.length === 0 && (
        <p className={styles.info}>Aucun jeu n'est actuellement associé à votre compte.</p>
      )}
    </div>
  );
};

export default PartnerPanelPage;